import { s__ } from '~/locale';
import { humanize } from '~/lib/utils/text_utility';
import { localeDateFormat } from '~/lib/utils/datetime/locale_dateformat';
import { isGid, getIdFromGraphQLId } from '~/graphql_shared/utils';
import {
  AGENT_PLATFORM_STATUS_ICON,
  AGENT_PLATFORM_STATUS_BADGE,
  MESSAGE_SUB_TYPE_DELEGATION,
  MESSAGE_SUB_TYPE_DELEGATION_RETURNS,
} from './constants';
import { parseToolInfo } from './icon_utils';

export const getNumericId = (id) => {
  if (!id) return null;
  return isGid(id) ? getIdFromGraphQLId(id) : id;
};

export { getToolData, getMessageData } from './icon_utils';

export const formatAgentDefinition = (agentDefinition) => {
  return humanize(agentDefinition || s__('DuoAgentsPlatform|Agent session'));
};

export const formatAgentFlowName = (agentDefinition, id) => {
  return `${formatAgentDefinition(agentDefinition)} #${id}`;
};

export const formatAgentStatus = (status) => {
  return status ? humanize(status.toLowerCase()) : s__('DuoAgentsPlatform|Unknown');
};

export const getAgentStatusIcon = (status) => {
  return AGENT_PLATFORM_STATUS_ICON[status] || AGENT_PLATFORM_STATUS_ICON.CREATED;
};

export const getAgentStatusBadge = (status) => {
  return AGENT_PLATFORM_STATUS_BADGE[status] || AGENT_PLATFORM_STATUS_BADGE.FAILED;
};

export const getNamespaceDatasetProperties = (dataset, properties) =>
  properties.reduce((acc, prop) => {
    acc[prop] = dataset[prop];
    return acc;
  }, {});

/**
 * Extracts component identity from a message for display.
 * Returns null when component attribution is not available (backward compat).
 */
export const getComponentLabel = (message) => {
  if (!message?.componentName) return null;
  return {
    componentName: message.componentName,
    subsessionId: message.subsessionId ?? null,
    isSubagent: Boolean(message.subsessionId),
  };
};

export const getDelegationData = (message) => {
  const toolInfo = parseToolInfo(message?.toolInfo);

  if (!toolInfo || toolInfo.name !== 'delegate_task') {
    return null;
  }

  return {
    subagentName: toolInfo.args?.subagent_name ?? null,
    subsessionId: toolInfo.args?.subsession_id?.toString() ?? null,
    prompt: toolInfo.args?.prompt ?? null,
  };
};

/**
 * Maps each unique `componentName` in `items` to a stable colour index based
 * on first-seen order. The same component name always gets the same index,
 * and a new name gets the next available index.
 *
 * Pure function so the algorithm can be unit-tested without mounting the
 * activity log component.
 */
export const buildComponentColorIndexMap = (items) => {
  return items.reduce((acc, item) => {
    const name = item?.componentName;
    if (name && !(name in acc)) {
      acc[name] = Object.keys(acc).length;
    }
    return acc;
  }, {});
};

/**
 * Maps each item index to its nesting depth. Entries between a delegation
 * and its matching `delegation_returns` are indented one level to visually
 * group subagent work.
 *
 * Pure function so the algorithm can be unit-tested without mounting the
 * activity log component.
 */
export const computeItemDepths = (items) => {
  let depth = 0;
  return items.map((item) => {
    if (item?.messageSubType === MESSAGE_SUB_TYPE_DELEGATION) {
      const current = depth;
      depth += 1;
      return current;
    }

    if (item?.messageSubType === MESSAGE_SUB_TYPE_DELEGATION_RETURNS) {
      depth = Math.max(0, depth - 1);
      return depth;
    }

    return depth;
  });
};

export const formatDate = (isoString) => {
  if (!isoString) return '';

  try {
    return localeDateFormat.asDate.format(new Date(isoString));
  } catch {
    return '';
  }
};
